import * as React from 'react';

import type { ISearchMatch } from '../api/participant-contracts';
import { slotWords } from '../roster-words';
import { markedWords, matchFieldWords, ROSTER_MATCH_FIELDS } from '../search-matches';
import { CardLink, recordPath } from './participant-link';

/**
 * Why a search row matched, drawn as survey-api sent it (SR-414, record search §6; the plan's
 * D17) — under a row's name on People, Teams, Firms and the register, and one line of it in the
 * top search. In the order sent, best rank first: nothing here filters, sorts, counts or ranks.
 *
 * **Two lines, then the rest folded in place** — "and 3 more reasons", a `<details>` rather than a
 * popover, because it sits in a table row, whose press opens the row (`DataTable` leaves a
 * `summary`'s press to the fold).
 *
 * A line is "<label>: <value>", the words of `q` marked in the value (`markedWords`, display
 * only). A roster field names the team or firm its slot is on, linked to that record under its
 * own list, with the slot in words; `member_of`'s value is that record's name, so the link is the
 * value. On a row of several participants, whose value it is is named after it. A note's snippet
 * is text, never markup — React escapes it.
 */
export function SearchMatches({
  matches,
  q,
  names,
  shown = 2
}: {
  matches: readonly ISearchMatch[];
  /** The search the row answers — whose words are marked. */
  q: string;
  /** Each participant's name on a row of several, to say whose value a match is; empty (or one)
   *  where the row is one record. */
  names?: ReadonlyMap<string, string>;
  /** How many lines are drawn before the fold. */
  shown?: number;
}) {
  if (matches.length === 0) return null;
  const first = matches.slice(0, shown);
  const rest = matches.slice(shown);
  return (
    <div data-matches className="mt-1 grid gap-0.5 text-xs text-muted-foreground">
      {first.map((match, index) => (
        <MatchLine key={index} match={match} q={q} names={names} />
      ))}
      {rest.length > 0 && (
        <details>
          <summary className="cursor-pointer text-[var(--color-sapphire)] underline-offset-4 hover:underline">
            and {rest.length} more {rest.length === 1 ? 'reason' : 'reasons'}
          </summary>
          <div className="mt-0.5 grid gap-0.5">
            {rest.map((match, index) => (
              <MatchLine key={index} match={match} q={q} names={names} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/** One reason, on one line — the top search draws the first alone. */
export function MatchLine({
  match,
  q,
  names,
  linked = true
}: {
  match: ISearchMatch;
  q: string;
  names?: ReadonlyMap<string, string>;
  /** Whether a slot's team or firm is a link — not inside an option that is itself one. */
  linked?: boolean;
}) {
  const label = matchFieldWords(match);
  const whose =
    names && names.size > 1 && match.participantId !== null
      ? names.get(match.participantId)
      : undefined;
  const value = <Marked value={match.value} q={q} rank={match.rank} />;
  const slot = match.slot;

  if (slot !== null && ROSTER_MATCH_FIELDS.has(match.field)) {
    const recordName = match.field === 'member_of' ? value : slot.displayName;
    const record = linked ? (
      <CardLink to={recordPath(slot.participantId, slot)}>{recordName}</CardLink>
    ) : (
      <span className="font-medium text-foreground">{recordName}</span>
    );
    return (
      <p data-match={match.field} className="wrap-anywhere">
        {label} {record} ({slotWords(slot)})
        {match.field !== 'member_of' && <>: {value}</>}
      </p>
    );
  }

  return (
    <p data-match={match.field} className="wrap-anywhere">
      {label}: {value}
      {whose !== undefined && <> · {whose}</>}
    </p>
  );
}

/** `value` with `q`'s words in `<mark>`, or plain where nothing is marked — a reason's value, or
 *  the top search's name where the name is the reason. */
export function Marked({ value, q, rank }: { value: string; q: string; rank: number }) {
  return (
    <span className="text-foreground">
      {markedWords(value, q, rank).map((part, index) =>
        part.marked ? (
          <mark
            key={index}
            className="rounded-sm bg-[color-mix(in_srgb,var(--color-yellow)_35%,white)] px-0.5 text-inherit"
          >
            {part.text}
          </mark>
        ) : (
          <React.Fragment key={index}>{part.text}</React.Fragment>
        )
      )}
    </span>
  );
}
